import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { PIPELINE_STAGES } from "@/lib/constants";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Target, MoreVertical, Pencil, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";

export default function Opportunities() {
  const [items, setItems] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stages, setStages] = useState(PIPELINE_STAGES);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/opportunities").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/client-projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/settings").then((r) => setStages(r.data.pipeline_stages || PIPELINE_STAGES)).catch(() => {});
  }, []);

  const schema = [
    { name: "titre", label: "Titre du projet commercial", required: true, col: 2 },
    { name: "client_id", label: "Client", type: "select", options: clients.map((c) => ({ value: c.id, label: c.nom })) },
    { name: "client_project_id", label: "Projet client", type: "select", options: projects.map((p) => ({ value: p.id, label: p.nom })) },
    { name: "montant_estime", label: "Montant potentiel (FCFA)", type: "number" },
    { name: "probabilite", label: "Probabilité (%)", type: "number" },
    { name: "date_cloture", label: "Date prévue de conclusion", type: "date" },
    { name: "stage", label: "Étape", type: "select", options: stages },
    { name: "commentaires", label: "Commentaires", type: "textarea" },
  ];

  const save = async (form) => {
    const client = clients.find((c) => c.id === form.client_id);
    const proj = projects.find((p) => p.id === form.client_project_id);
    const payload = {
      ...form,
      client_name: client ? client.nom : (form.client_name || ""),
      client_project_name: proj ? proj.nom : "",
    };
    try {
      if (editing) await api.put(`/opportunities/${editing.id}`, payload);
      else await api.post("/opportunities", payload);
      toast.success(editing ? "Opportunité mise à jour" : "Opportunité créée"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const patchStage = async (o, stage) => {
    try { const { data } = await api.patch(`/opportunities/${o.id}/stage`, { stage }); setItems((prev) => prev.map((x) => (x.id === o.id ? data : x))); if (data.converted) { toast.success("Opportunité gagnée : client créé automatiquement 🎉"); load(); } else { toast.success("Étape mise à jour"); } }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/opportunities/${id}`); toast.success("Supprimé"); load(); };
  const convert = async (id) => {
    if (!window.confirm("Convertir cette opportunité en client ? Elle sera marquée « Gagné ».")) return;
    try { await api.post(`/opportunities/${id}/convert-to-client`); toast.success("Client créé depuis l'opportunité"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const totalPondere = (items || []).reduce((s, o) => s + (o.valeur_ponderee || 0), 0);

  return (
    <div>
      <PageHeader title="Opportunités" subtitle="Suivi des opportunités commerciales & valeur pondérée">
        <div className="rounded-lg border bg-white px-4 py-2 mr-2 hidden sm:block">
          <p className="text-[10px] uppercase font-mono text-slate-400">Valeur pondérée totale</p>
          <p className="font-mono font-bold text-brand-600">{fmtMoney(totalPondere)}</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-opportunity" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvelle opportunité</Button>
      </PageHeader>

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={Target} title="Aucune opportunité" subtitle="Transformez vos prospects qualifiés en opportunités." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>N°</TableHead><TableHead>Titre</TableHead><TableHead>Client</TableHead><TableHead>Projet client</TableHead>
                <TableHead>Montant</TableHead><TableHead>Valeur pondérée</TableHead>
                <TableHead className="min-w-44">Étape</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((o) => (
                  <TableRow key={o.id} className="hover:bg-slate-50" data-testid={`opp-row-${o.id}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{o.numero}</TableCell>
                    <TableCell className="font-semibold">{o.titre}</TableCell>
                    <TableCell className="text-slate-600">{o.client_name || o.prospect_name || "—"}</TableCell>
                    <TableCell className="text-slate-600">{o.client_project_name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{fmtMoney(o.montant_estime)}</TableCell>
                    <TableCell className="font-mono text-xs font-bold text-brand-600">{fmtMoney(o.valeur_ponderee)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Select value={o.stage} onValueChange={(v) => patchStage(o, v)}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`opp-stage-${o.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        {o.converted_client_id && <span title="Converti en client" className="shrink-0 inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700"><UserCheck className="h-3 w-3" /></span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`opp-menu-${o.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!o.converted_client_id && <DropdownMenuItem onClick={() => convert(o.id)} data-testid={`opp-convert-${o.id}`}><UserCheck className="h-4 w-4 mr-2" />Convertir en client</DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => { setEditing(o); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => remove(o.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier l'opportunité" : "Nouvelle opportunité"} schema={schema} initial={editing} onSubmit={save} />
    </div>
  );
}
